/**
 * Modul "Blacklist": Name, Ausweisnummer, Datum, Grund (Freitext). Rechte-
 * Modell (siehe Migration 0055, Kategorie "Blacklist"):
 *   - blacklist.manage   Ansehen, Anlegen, Bearbeiten - alle aktuell
 *                        existierenden regulären Rollen AUSSER
 *                        "Routenpartner".
 *   - blacklist.delete   Löschen - nur President/VizePresident/Phantom.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { formatDate, formatDateTime } from "../../utils/formatDate";
import type { BlacklistEntry } from "../../types";

export default function BlacklistPage() {
  const canDelete = usePermission("blacklist.delete");
  const [entries, setEntries] = useState<BlacklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<BlacklistEntry | null | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await api.get<BlacklistEntry[]>("/api/blacklist"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(entry: BlacklistEntry) {
    if (!confirm(`"${entry.name}" wirklich von der Blacklist entfernen?`)) return;
    try {
      await api.delete(`/api/blacklist/${entry.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">🚫 Blacklist</h1>
          <p className="text-sm text-gray-500 mt-1">Gesperrte Personen des Clubs.</p>
        </div>
        <button className="btn-primary" onClick={() => setEditing(null)}>+ Eintrag hinzufügen</button>
      </div>

      <Card padded={false} className="overflow-x-auto">
        {loading ? (
          <Spinner />
        ) : entries.length === 0 ? (
          <EmptyState message="Keine Einträge auf der Blacklist." />
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>Name</th>
                <th>Ausweisnummer</th>
                <th>Datum</th>
                <th>Grund</th>
                <th>Erstellt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="font-medium text-gray-100 whitespace-nowrap">{entry.name}</td>
                  <td className="text-gray-400 whitespace-nowrap">{entry.id_number ?? "—"}</td>
                  <td className="text-gray-400 whitespace-nowrap">{entry.entry_date ? formatDate(entry.entry_date) : "—"}</td>
                  <td className="text-gray-300 max-w-sm">
                    <p className="line-clamp-2 whitespace-pre-wrap break-words">{entry.reason}</p>
                  </td>
                  <td className="text-gray-500 text-xs whitespace-nowrap">
                    {formatDateTime(entry.created_at)}
                    {entry.created_by_name && <span className="block text-gray-600">von {entry.created_by_name}</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button className="text-accent text-xs hover:underline mr-3" onClick={() => setEditing(entry)}>Bearbeiten</button>
                    {canDelete && <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(entry)}>Löschen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {editing !== undefined && (
        <BlacklistForm entry={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}

function BlacklistForm({ entry, onClose, onSaved }: { entry: BlacklistEntry | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!entry;
  const [name, setName] = useState(entry?.name ?? "");
  const [idNumber, setIdNumber] = useState(entry?.id_number ?? "");
  const [entryDate, setEntryDate] = useState(entry?.entry_date ?? "");
  const [reason, setReason] = useState(entry?.reason ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = { name, idNumber: idNumber || null, entryDate: entryDate || null, reason };
      if (isEdit) await api.put(`/api/blacklist/${entry!.id}`, payload);
      else await api.post("/api/blacklist", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? "Eintrag bearbeiten" : "Neuer Blacklist-Eintrag"}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
        <button className="btn-primary" form="blacklist-entry-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
      </>}
    >
      <form id="blacklist-entry-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Name *</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Ausweisnummer</label>
            <input className="input" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
          </div>
          <div>
            <label className="label">Datum</label>
            <input className="input" type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Grund *</label>
          <textarea className="input" rows={5} value={reason} onChange={(e) => setReason(e.target.value)} required />
        </div>
      </form>
    </Modal>
  );
}
